import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Download,
  FileJson,
  FileSpreadsheet,
  FileCode,
  Copy,
  Code2,
} from 'lucide-react';
import { toast } from 'sonner';
import { downloadFile, jsonToCSV, jsonToXML, generateCodeSnippet } from '@/utils/export-utils';

interface ExportMenuProps {
  data: any;
  nodeName?: string;
  className?: string;
}

export const ExportMenu: React.FC<ExportMenuProps> = ({ data, nodeName, className }) => {
  const [showFilenameDialog, setShowFilenameDialog] = useState(false);
  const [filename, setFilename] = useState('');
  const [exportFormat, setExportFormat] = useState<'json' | 'csv' | 'xml'>('json');

  const handleCopyJSON = () => {
    if (data) {
      navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      toast.success('JSON copied to clipboard');
    }
  };

  const handleCopyCSV = () => {
    try {
      const csv = jsonToCSV(data);
      if (!csv) {
        toast.error('Data cannot be converted to CSV format');
        return;
      }
      navigator.clipboard.writeText(csv);
      toast.success('CSV copied to clipboard');
    } catch (error) {
      toast.error('Failed to convert to CSV: ' + (error as Error).message);
    }
  };

  const handleCopyXML = () => {
    try {
      const xml = jsonToXML(data, nodeName || 'data');
      navigator.clipboard.writeText(xml);
      toast.success('XML copied to clipboard');
    } catch (error) {
      toast.error('Failed to convert to XML: ' + (error as Error).message);
    }
  };

  const handleCopyCodeSnippet = (language: 'javascript' | 'python') => {
    try {
      const snippet = generateCodeSnippet(data, language);
      navigator.clipboard.writeText(snippet);
      toast.success(`${language === 'javascript' ? 'JavaScript' : 'Python'} code copied to clipboard`);
    } catch (error) {
      toast.error('Failed to generate code snippet: ' + (error as Error).message);
    }
  };

  const handleDownload = (format: 'json' | 'csv' | 'xml') => {
    setExportFormat(format);
    const defaultFilename = `${nodeName || 'export'}-${Date.now()}`;
    setFilename(defaultFilename);
    setShowFilenameDialog(true);
  };

  const handleConfirmDownload = () => {
    try {
      let content = '';
      let mimeType = '';
      let extension = '';

      switch (exportFormat) {
        case 'json':
          content = JSON.stringify(data, null, 2);
          mimeType = 'application/json';
          extension = '.json';
          break;
        case 'csv':
          content = jsonToCSV(data);
          if (!content) {
            toast.error('Data cannot be converted to CSV format');
            return;
          }
          mimeType = 'text/csv';
          extension = '.csv';
          break;
        case 'xml':
          content = jsonToXML(data, nodeName || 'data');
          mimeType = 'application/xml';
          extension = '.xml';
          break;
      }

      // Add extension if not present
      let finalFilename = filename;
      if (!finalFilename.endsWith(extension)) {
        finalFilename += extension;
      }

      downloadFile(content, finalFilename, mimeType);
      toast.success(`Downloaded ${finalFilename}`);
      setShowFilenameDialog(false);
    } catch (error) {
      toast.error('Failed to download file: ' + (error as Error).message);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className={className}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>Copy to Clipboard</DropdownMenuLabel>
          <DropdownMenuItem onClick={handleCopyJSON}>
            <FileJson className="h-4 w-4 mr-2" />
            Copy as JSON
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleCopyCSV}>
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Copy as CSV
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleCopyXML}>
            <FileCode className="h-4 w-4 mr-2" />
            Copy as XML
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuLabel>Copy as Code</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => handleCopyCodeSnippet('javascript')}>
            <Code2 className="h-4 w-4 mr-2" />
            JavaScript
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleCopyCodeSnippet('python')}>
            <Code2 className="h-4 w-4 mr-2" />
            Python
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuLabel>Download File</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => handleDownload('json')}>
            <FileJson className="h-4 w-4 mr-2" />
            Download as JSON
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleDownload('csv')}>
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Download as CSV
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleDownload('xml')}>
            <FileCode className="h-4 w-4 mr-2" />
            Download as XML
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Filename Dialog */}
      <Dialog open={showFilenameDialog} onOpenChange={setShowFilenameDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Download File</DialogTitle>
            <DialogDescription>
              Enter a filename for your {exportFormat.toUpperCase()} export
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="filename">Filename</Label>
              <Input
                id="filename"
                value={filename}
                onChange={(e) => setFilename(e.target.value)}
                placeholder={`export.${exportFormat}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleConfirmDownload();
                  }
                }}
              />
              <p className="text-xs text-muted-foreground">
                Extension .{exportFormat} will be added automatically if not present
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFilenameDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmDownload}>
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ExportMenu;
